import type React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Stack,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EventIcon from "@mui/icons-material/Event";
import StreamIcon from "@mui/icons-material/Stream";
import BrandingWatermarkIcon from "@mui/icons-material/BrandingWatermark";
import CustomTextField from "../../../components/ui/Textfield";
import CustomSelect from "../../../components/ui/Select";
import { BookingData } from "../../../types/bookings";

interface ConferenceBookingFormProps {
  bookingData: BookingData;
  setBookingData: React.Dispatch<React.SetStateAction<BookingData>>;
  serviceType: string;
}
const ConferenceBookingForm: React.FC<ConferenceBookingFormProps> = ({
  bookingData,
  setBookingData,
  serviceType: _serviceType,
}) => {
  void _serviceType;
  const handleInputChange = (
    field: keyof BookingData,
    value: BookingData[keyof BookingData],
  ) => {
    setBookingData((prev) => ({ ...prev, [field]: value }));
  };
  console.log("conference_type value:", bookingData.conference_type);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Basic Conference Information */}
        <Card sx={{ mb: 3, boxShadow: 0 }}>
          <CardContent>
            <Stack spacing={3}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <EventIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6" fontWeight={600}>
                  Basic Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  {" "}
                  <CustomTextField
                    label="Conference Name"
                    value={bookingData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth>
                    <CustomSelect
                      label="Conference Type"
                      value={bookingData.conference_type || ""}
                      onChange={(e) =>
                        handleInputChange("conference_type", e.target.value)
                      }
                    >
                      <MenuItem value="summit">Summit</MenuItem>
                      <MenuItem value="workshop">Workshop</MenuItem>
                      <MenuItem value="symposium">Symposium</MenuItem>
                      <MenuItem value="dinner">Dinner</MenuItem>
                      <MenuItem value="breakfast_meeting">
                        Breakfast Meeting
                      </MenuItem>
                      <MenuItem value="congress">Congress</MenuItem>
                      <MenuItem value="conference">Conference</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </CustomSelect>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 2 }}>
                  <CustomTextField
                    label="Start Date"
                    value={bookingData.start_date_of_event}
                    onChange={(e) =>
                      handleInputChange("start_date_of_event", e.target.value)
                    }
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 2 }}>
                  <CustomTextField
                    label="End Date"
                    type="date"
                    value={bookingData.end_date_of_event}
                    onChange={(e) =>
                      handleInputChange("end_date_of_event", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 2 }}>
                  <CustomTextField
                    label="Start Time"
                    type="time"
                    value={bookingData.start_time}
                    onChange={(e) =>
                      handleInputChange("start_time", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 2 }}>
                  <CustomTextField
                    label="End Time"
                    value={bookingData.end_time}
                    type="time"
                    onChange={(e) =>
                      handleInputChange("end_time", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <CustomTextField
                      label="Conference Description"
                      value={bookingData.description || ""}
                      onChange={(e) =>
                        handleInputChange("description", e.target.value)
                      }
                      multiline
                      rows={3}
                      variant="outlined"
                      placeholder="Briefly describe the purpose and agenda of the conference..."
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <CustomTextField
                    fullWidth
                    label="Expected Audience Size"
                    value={bookingData.expected_audience_size}
                    onChange={(e) =>
                      handleInputChange(
                        "expected_audience_size",
                        e.target.value,
                      )
                    }
                    type="number"
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <FormLabel>Access Type</FormLabel>
                    <RadioGroup
                      row
                      value={bookingData.access_type}
                      onChange={(e) =>
                        handleInputChange("access_type", e.target.value)
                      }
                    >
                      <FormControlLabel
                        value="public"
                        control={<Radio />}
                        label="Public"
                      />
                      <FormControlLabel
                        value="private"
                        control={<Radio />}
                        label="Private"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        {/* Streaming Configuration */}
        <Accordion sx={{ mb: 3, boxShadow: 0 }} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <StreamIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Streaming & Recording
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!bookingData.enable_livestreaming}
                    onChange={(e) =>
                      handleInputChange(
                        "enable_livestreaming",
                        e.target.checked,
                      )
                    }
                  />
                }
                label="Enable live streaming"
              />

              {bookingData.enable_livestreaming && (
                <>
                  <FormControl fullWidth>
                    <CustomSelect
                      value={bookingData?.streaming_platform || ""}
                      onChange={(e) =>
                        handleInputChange("streaming_platform", e.target.value)
                      }
                      label="Streaming Platform"
                    >
                      {" "}
                      <MenuItem value="youtube">Youtube</MenuItem>
                      <MenuItem value="zoom">Zoom</MenuItem>
                      <MenuItem value="teams">Microsoft Teams</MenuItem>
                      <MenuItem value="webex">Cisco Webex</MenuItem>
                      <MenuItem value="custom">Custom Platform</MenuItem>
                      <MenuItem value="provide">
                        We&apos;ll provide platform
                      </MenuItem>
                    </CustomSelect>
                  </FormControl>

                  <FormControl fullWidth>
                    <CustomSelect
                      value={bookingData.session_to_stream}
                      onChange={(e) =>
                        handleInputChange("session_to_stream", e.target.value)
                      }
                      label="Sessions to Stream"
                    >
                      <MenuItem value="all">All sessions</MenuItem>
                      <MenuItem value="main">Main sessions only</MenuItem>
                      <MenuItem value="specific">Specific sessions</MenuItem>
                    </CustomSelect>
                  </FormControl>

                  {bookingData.session_to_stream === "specific" && (
                    <CustomTextField
                      fullWidth
                      label="Specify sessions to stream"
                      value={bookingData.specific_sessions}
                      onChange={(e) =>
                        handleInputChange("specific_sessions", e.target.value)
                      }
                      multiline
                      rows={2}
                      placeholder="List the specific sessions you want to stream..."
                    />
                  )}

                  <Divider />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!bookingData.record_stream}
                        onChange={(e) =>
                          handleInputChange("record_stream", e.target.checked)
                        }
                      />
                    }
                    label="Record the stream for later viewing"
                  />

                  {bookingData.record_stream && (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!bookingData.provide_on_demand_access}
                            onChange={(e) =>
                              handleInputChange(
                                "provide_on_demand_access",
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label="Provide on-demand access after event"
                      />

                      {bookingData.provide_on_demand_access && (
                        <CustomTextField
                          fullWidth
                          label="Retention Duration"
                          value={bookingData.retention_duration}
                          onChange={(e) =>
                            handleInputChange(
                              "retention_duration",
                              e.target.value,
                            )
                          }
                          placeholder="e.g., 30 days, 6 months, 1 year"
                          helperText="How long should recordings be available?"
                        />
                      )}
                    </>
                  )}
                  {/* Stream Program Upload */}
                </>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Branding & Customization */}
        <Accordion sx={{ mb: 3, boxShadow: 0 }} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <BrandingWatermarkIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Branding & Customization
              </Typography>
              {bookingData.require_custom_branding && (
                <Chip
                  label="Required"
                  size="small"
                  color="secondary"
                  sx={{ ml: 2 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!bookingData.require_custom_branding}
                    onChange={(e) =>
                      handleInputChange(
                        "require_custom_branding",
                        e.target.checked,
                      )
                    }
                  />
                }
                label="Custom branding required"
              />

              {bookingData.require_custom_branding && (
                <CustomTextField
                  fullWidth
                  label="Branding Requirements"
                  value={bookingData.branding_requirements}
                  onChange={(e) =>
                    handleInputChange("branding_requirements", e.target.value)
                  }
                  multiline
                  rows={3}
                  placeholder="Describe your branding needs (logos, colors, themes, etc.)"
                />
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Interactive Features
        <Accordion sx={{ mb: 3, boxShadow: 0 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <InteractiveIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Interactive Features
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  Select interactive features needed:
                </FormLabel>
                <FormGroup row>
                  {[
                    "Q&A",
                    "Live Chat",
                    "Polls",
                    "Breakout Rooms",
                    "Screen Sharing",
                    "Whiteboard",
                  ].map((feature) => (
                    <FormControlLabel
                      key={feature}
                      control={
                        <Checkbox
                          checked={bookingData.interactiveFeatures.includes(
                            feature
                          )}
                          onChange={(e) =>
                            handleInteractiveFeatureChange(
                              feature,
                              e.target.checked
                            )
                          }
                        />
                      }
                      label={feature}
                    />
                  ))}
                </FormGroup>
              </FormControl>

              {bookingData.interactiveFeatures.length > 0 && (
                <CustomTextField
                  fullWidth
                  label="Additional Interactive Requirements"
                  value={bookingData.interactiveNeeds}
                  onChange={(e) =>
                    handleInputChange("interactiveNeeds", e.target.value)
                  }
                  multiline
                  rows={3}
                  placeholder="Describe any specific requirements for the interactive features..."
                />
              )}
            </Stack>
          </AccordionDetails>
        </Accordion> */}
      </Box>
    </LocalizationProvider>
  );
};

export default ConferenceBookingForm;
